/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-01 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import {
  Page,
  // PageActions,
  Layout,
  Stack,
  Button
} from '@shopify/polaris';
import { connect } from 'react-redux';
import { reduxForm, reset, getFormValues } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import InCollectionSearch from 'src/components/views/search-settings/in-collection-search';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import SearchSettingProcessingBanner from 'src/components/views/search-settings/setting-processing-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';

import * as SearchSettingsActions from 'actions/search-settings-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import { confirmLeavePage } from 'src/helpers';
import {
  buildInitialSearchSettingsValues,
  buildInitialSearchSettingsV2Values,
  buildSearchSettingsValues,
  allowUsingNewSearchSettings
} from 'src/helpers/search-settings-helper';

import type { ComposedProps, State } from './index.type';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/search-settings/did-you-know-collection';
import history from 'src/routes/history';

class InCollectionSearchSetting extends Component<ComposedProps, State> {
  state = {
    activeInfoBanner: true,
    selected: 0,
    discardState: 0,
    youtubeModal: false,
    indexStatusChanged: null,
    optionChange: ''
  };

  componentDidMount() {
    const { getSearchSettings } = this.props;

    getSearchSettings();
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { updateLoading, scheduleSyncIn, waitSyncAt } = nextProps;
    const { updateLoading: updateLoadingPrev, getSyncProcessStatus, updateShowBanner } = this.props;

    // Check to show banner Processing update when change status/ import
    if (updateLoadingPrev && !updateLoading) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      submitting,
      handleSubmit,
      pristine,
      updateLoading,
      dirty,
      getLoading,
      updateSuccess,
      enableCollectionSearch,
      showScheduleSync,
      selectedPlan,
      allowSearchV2,
      syncDuration
    } = this.props;
    const { discardState, youtubeModal } = this.state;

    const docUrl = allowSearchV2
      ? URL.Link_V2_Search_SearchSetting_ManualGuide
      : URL.Link_V1_Search_SearchSetting_ManualGuide;

    return (
      <div className="BC-Page BC-Page__InCollectionSettings">
        <Page
          title={i18n.translate('AdminHTML.SearchSettings.InCollectionSearch.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.discardChange}>
                    {i18n.translate('AdminHTML.Common.Action.discard')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={updateLoading && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.saveSettings')}
                </Button>
              </Stack>
            </div>
          }>
          <TitleHelp i18n={i18n} hrefUserManual={docUrl} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Did you know */}
                <DidYouKnow />
                {/* Processing update banner */}
                {showScheduleSync && (
                  <SearchSettingProcessingBanner
                    syncDuration={syncDuration}
                    featureName="inCollection"
                  />
                )}
              </div>
            </Layout.Section>

            <Layout.Section>
              <div className="Children-Card__Section">
                <InCollectionSearch
                  enableCollectionSearch={enableCollectionSearch}
                  loading={getLoading}
                />
              </div>
            </Layout.Section>
          </Layout>
          {/* <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: pristine || submitting,
              loading: updateLoading && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.discard'),
                      onAction: this.discardChange,
                      disabled: submitting
                    }
                  ]
                : []
            }
          /> */}
        </Page>

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['SearchSettingsTut']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate('AdminHTML.SearchSettings.SearchSettings.youtubeModal_Title')}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded
            type="search-settings"
            conditionShow={updateSuccess}
            conditionShowStateChange={discardState}
          />
        )}

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Handle click save button
  handleSave = async data => {
    const {
      updateSearchSettings,
      searchSettings,
      getChecklist,
      themeSetupV2 = false,
      markDoneStep
    } = this.props;
    const saveData = buildSearchSettingsValues(data, searchSettings, themeSetupV2);

    markDoneStep('V2_SEARCH1', {
      isDone: true
    });

    await updateSearchSettings(saveData);
    mixpanelV3Helper.trackInCollectionSetting(searchSettings, saveData);
    await getChecklist();
  };

  // Handle discard button
  discardChange = () => {
    const { resetForm } = this.props;
    const { discardState } = this.state;

    resetForm();
    this.setState({ discardState: discardState + 1 });
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const InCollectionForm = reduxForm({
  form: 'InCollectionForm', // a unique identifier for this form
  enableReinitialize: true
})(InCollectionSearchSetting);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSearchSettings: () => dispatch(SearchSettingsActions.getSearchSettings(ownProps.i18n)),
  updateSearchSettings: (searchSettings: Object) =>
    dispatch(SearchSettingsActions.updateSearchSettings(searchSettings, undefined, ownProps.i18n)),
  resetForm: () => dispatch(reset('InCollectionForm')),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { searchSettings, oauth = {} } = state;
  const themeSetupV2 = _.get(oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);
  const searchVersion = _.get(oauth, 'tenantApp.parseParams.searchVersion', '1.0');

  const allowSearchV2 = allowUsingNewSearchSettings(searchVersion);
  const formValues = getFormValues('InCollectionForm')(state);
  const initialValues = allowSearchV2
    ? buildInitialSearchSettingsV2Values(searchSettings.searchSettings, themeSetupV2)
    : buildInitialSearchSettingsValues(searchSettings.searchSettings, themeSetupV2);

  return {
    themeSetupV2,
    formValues,
    allowSearchV2,
    migrateStatus: _.get(state.common, 'status', null),
    enableCollectionSearch: _.get(formValues, 'inCollectionSearch.enableCollectionSearch', false),
    searchSettings: _.get(searchSettings, 'searchSettings', {}),
    getLoading: _.get(searchSettings, 'getLoading', false),
    updateSuccess: _.get(searchSettings, 'updateSuccess', false),
    updateLoading: _.get(searchSettings, 'updateLoading', false),
    scheduleSyncIn: _.get(searchSettings, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(searchSettings, 'waitSyncAt', 0),
    initialValues,
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan')
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(InCollectionForm)),
  'MapProduct'
);
